import { forwardRef } from "react";

/**
 * Shared 3-card grid for picking a project render mode.
 *
 * Used in two places:
 *   1. EditorList.jsx CreateDialog — new project, all cards clickable.
 *   2. EditorEdit.jsx ModeSwitchDialog — change existing project's mode.
 *      Pass `currentMode` so the active card gets a "Aktueller Modus"
 *      badge and is disabled (clicking it would be a no-op).
 *
 * Card ids match the backend enum: fastcut | hybrid | full.
 *
 * @param {string} value          currently selected mode (highlighted ring)
 * @param {(id:string)=>void} onSelect callback fired on card click
 * @param {string} [currentMode]  the project's actual mode — that card
 *                                gets an "Aktueller Modus" ribbon and is
 *                                non-clickable. Omit for the create flow.
 * @param {string} [testidPrefix] used to build data-testids, e.g.
 *                                "editor-mode-card" → editor-mode-card-fastcut.
 *                                Defaults to "editor-mode-card".
 */
export const MODE_OPTIONS = [
  {
    id: "fastcut",
    icon: "\u26A1",
    title: "Schnellschnitt",
    time: "10-30 Sek",
    features: [
      { ok: true, label: "Clips ordnen" },
      { ok: true, label: "Kürzen" },
      { ok: true, label: "Musik" },
      { ok: false, label: "Übergänge" },
      { ok: false, label: "Text / POI" },
      { ok: false, label: "Titelkarten" },
    ],
  },
  {
    id: "hybrid",
    icon: "\uD83D\uDE80",
    title: "Übergänge",
    time: "1-3 Min",
    features: [
      { ok: true, label: "Clips ordnen" },
      { ok: true, label: "Kürzen" },
      { ok: true, label: "Musik" },
      { ok: true, label: "Übergänge" },
      { ok: false, label: "Text / POI" },
      { ok: false, label: "Titelkarten" },
    ],
  },
  {
    id: "full",
    icon: "\uD83C\uDFAC",
    title: "Voll-Editor",
    time: "5-15 Min",
    features: [
      { ok: true, label: "Clips ordnen" },
      { ok: true, label: "Kürzen" },
      { ok: true, label: "Musik" },
      { ok: true, label: "Übergänge" },
      { ok: true, label: "Text / POI" },
      { ok: true, label: "Titelkarten" },
    ],
  },
];

const ModeSelectorCards = forwardRef(function ModeSelectorCards(
  { value, onSelect, currentMode, testidPrefix = "editor-mode-card" },
  ref,
) {
  return (
    <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {MODE_OPTIONS.map((opt) => {
        const active = value === opt.id;
        const isCurrent = currentMode === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            disabled={isCurrent}
            onClick={() => !isCurrent && onSelect(opt.id)}
            data-testid={`${testidPrefix}-${opt.id}`}
            className={`relative text-left p-4 rounded-xl border transition-all ${
              isCurrent
                ? "border-[#3a3a44] bg-[#1a1a1e] cursor-not-allowed opacity-70"
                : active
                  ? "border-[#ff6b35] bg-[#ff6b35]/10 ring-2 ring-[#ff6b35]/30"
                  : "border-[#2a2a30] bg-[#1f1f23] hover:border-[#3a3a44]"
            }`}
          >
            {isCurrent && (
              <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wide text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                ✓ Aktuell
              </span>
            )}
            <div className="flex items-center gap-2 text-2xl">
              <span>{opt.icon}</span>
              <span className="text-base font-semibold text-white">{opt.title}</span>
            </div>
            <div className="mt-1 text-xs text-[#9a9aa3]">Rendern in ~{opt.time}</div>
            <ul className="mt-3 space-y-1 text-xs">
              {opt.features.map((f, i) => (
                <li
                  key={i}
                  className={f.ok ? "text-emerald-300" : "text-[#5c5c67]"}
                >
                  {f.ok ? "✓" : "✗"} {f.label}
                </li>
              ))}
            </ul>
          </button>
        );
      })}
    </div>
  );
});

export default ModeSelectorCards;
